import { action, computed, makeObservable, observable, reaction } from 'mobx';
import LocalStorage from '../services/LocalStorage';

/**
 * !IMPORTANT
 * This class can work only with @Observable decorator and not @StoreFieldInSession decorator.
 */

export default abstract class AbstractSessionModel {
  private __sessionFields: Map<
    string,
    {
      value: any;
      key: string;
      lateKeyInitilization?: boolean;
      type?: 'Set';
      serialize?: (value: any) => any;
      deserialize?: (value: any) => any;
      reactOn?(value: any): any;
    }
  >;

  private __observedFields: Set<string>;

  private __actionFields: Set<string>;

  private __computedFields: Set<string>;

  private wizardSessionDataKey: string = 'wizardSessionData';

  abstract getSessionKey(): string;

  constructor() {}

  protected initFields() {
    //[1] set intial values for session fields
    let wizardSessionData =
      LocalStorage.getJson(this.wizardSessionDataKey) || {};

    if (this.__sessionFields !== void 0) {
      for (let [key, value] of this.__sessionFields) {
        value.key = `${this.getSessionKey()}.${key}`;

        // @TODO - remove lateKeyInitilization and use the key directly
        //if (value.lateKeyInitilization) {
        //value.key = `${(this as any).__getSessionKey()}.${key}`;
        //}

        // check if the value is already in session storage
        if (wizardSessionData[value.key] !== void 0) {
          // check if value is deserializable
          if (value.deserialize !== void 0) {
            this[key] = value.deserialize(wizardSessionData[value.key]);
            continue;
          }

          // check if the value is a set
          if (
            Object.prototype.toString.call(wizardSessionData[value.key]) ===
              '[object Object]' &&
            wizardSessionData[value.key].__type__ === 'Set'
          ) {
            this[key] = new Set(wizardSessionData[value.key].__data__);
            continue;
          }

          if (
            Object.prototype.toString.call(wizardSessionData[value.key]) ===
              '[object Object]' &&
            wizardSessionData[value.key].__type__ === 'Map'
          ) {
            this[key] = new Map(wizardSessionData[value.key].__data__);
            continue;
          }

          // load value from session storage
          this[key] = wizardSessionData[value.key];
        }
      }
    }

    if (this.__observedFields !== void 0) {
      let mobxConfig = {};

      for (let [key, _] of this.__observedFields.entries()) {
        mobxConfig[key] = observable;
      }

      makeObservable(this, mobxConfig);

      // attach reaction to save session data
      reaction(
        () => {
          let data = {};

          if (this.__sessionFields === void 0) {
            return data;
          }

          for (let [key, value] of this.__sessionFields.entries()) {
            // check if this field is observed
            if (this.__observedFields.has(key) === false) {
              throw new Error(
                `Field ${key} is not observed. Please add @Observable decorator to the field`
              );
            }

            data[key] = this[key];

            if (value.reactOn !== void 0) {
              data[`${key}._reaction`] = value.reactOn(data[key]);
              continue;
            }

            if (Array.isArray(data[key])) {
              // react the lenght of the array
              data[`_${key}.lenght._reaction`] = data[key].length;
              // react to all the elements of the array
              data[`_${key}.string._reaction`] = JSON.stringify(data[key]);
              continue;
            }

            // if the value is object
            if (typeof data[key] === 'object') {
              // react to the all the keys of the object
              let stringified = JSON.stringify(data[key]);
              data[`_${key}.object._reaction`] = stringified;
              continue;
            }
          }

          return data;
        },
        (data) => {
          let newSessionData =
            LocalStorage.getJson(this.wizardSessionDataKey) || {};

          Object.keys(data).forEach((key) => {
            if (this.__sessionFields.has(key) === false) {
              return;
            }

            let _key = this.__sessionFields.get(key).key;
            let type = this.__sessionFields.get(key).type;

            // check if type is set
            if (type === 'Set') {
              // convert the set to array
              newSessionData[_key] = {
                __data__: Array.from(data[key]),
                __type__: 'Set'
              };
              return;
            }

            if (type === 'Map') {
              let map = data[key];

              newSessionData[_key] = {
                __data__: Array.from(map),
                __type__: 'Map'
              };

              return;
            }

            // check if the value is serialized
            let serialize = this.__sessionFields.get(key).serialize;
            if (serialize !== void 0) {
              newSessionData[_key] = serialize(data[key]);
              return;
            }

            newSessionData[_key] = data[key];
          });

          LocalStorage.storeJson(this.wizardSessionDataKey, newSessionData);
        },
        {
          fireImmediately: true
        }
      );
    }

    if (this.__actionFields !== void 0) {
      let mobxConfig = {};

      for (let [key, _] of this.__actionFields.entries()) {
        mobxConfig[key] = action;
      }

      makeObservable(this, mobxConfig);
    }

    if (this.__computedFields !== void 0) {
      let mobxConfig = {};

      for (let [key, _] of this.__computedFields.entries()) {
        mobxConfig[key] = computed;
      }

      makeObservable(this, mobxConfig);
    }
  }
}
